import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useActivate, useUnactivate } from 'react-activation'
import { Cascader, Spin, Select, message, Tooltip, Image, Popconfirm } from 'antd'
import { useSelector } from 'react-redux'
import classNames from 'classnames'
import { getChannelListApi } from '@/service/channel/api/getChannelList'
import type { ChannelItemRes } from '@/service/channel/types/getChannelList'
import { getRoomListApi } from '@/service/liveChat/api/getRoomList'
import type { RoomItemRes } from '@/service/liveChat/type/getRoomList'
import { getRoomDetailApi } from '@/service/liveChat/api/getRoomDetail'
import { sendRoomMessageApi } from '@/service/liveChat/api/sendRoomMessage'
import type { RoomDetailData, RoomDetailMessage } from '@/service/liveChat/type/getRoomDetail'
import { getRoomAgentsApi } from '@/service/liveChat/api/getRoomAgents'
import { handoverApi } from '@/service/liveChat/api/handover'
import { closeRoomApi } from '@/service/liveChat/api/closeRoom'
import { uploadFileApi } from '@/service/liveChat/api/uploadFile'
import type { UploadFileReq } from '@/service/liveChat/type/uploadFile'
import { addRoomTagApi } from '@/service/liveChat/api/addRoomTag'
import { deleteRoomMessageApi } from '@/service/liveChat/api/deleteRoomMessage'
import { updateAgentStatusApi } from '@/service/liveChat/api/updateAgentStatus'
import type { AgentStatus } from '@/service/liveChat/type/updateAgentStatus'
import { useWebSocket } from '@/hooks/use-websocket'
import { onLogout } from '@/pages/layout'
import whatsappIcon from '@/assets/images/whatsapp.png'
import telegramIcon from '@/assets/images/telegram.png'
import livechatIcon from '@/assets/images/livechat.png'
import copyIcon from '@/assets/images/copy.png'
import SvgIcon from '@/components/svg-icon'
import userIcon from '@/assets/images/chat-user.png'
import robotIcon from '@/assets/images/chat-ai.png'
import agentIcon from '@/assets/images/chat-agent.png'
import searchIcon from '@/assets/images/search-icon.jpg'
import fileIcon from '@/assets/images/upfile.png'
import photoIcon from '@/assets/images/upphoto.png'
import videoIcon from '@/assets/images/upvedio.png'
import lineIcon from '@/assets/images/line.png'
import styles from './index.module.scss'
import { copyToClipboard } from '@/utils/clipboard'

const CHANNEL_TYPES = ['Telegram', 'WhatsApp', 'WhatsAppPersonal', 'WebWidget', 'Line'] as const

const typeIconMap: Record<string, string> = {
    Telegram: telegramIcon,
    WhatsApp: whatsappIcon,
    WhatsAppPersonal: whatsappIcon,
    WebWidget: livechatIcon,
    Line: lineIcon,
}

const AVATAR_COLORS = [
    'linear-gradient(135deg, #6366f1, #8b5cf6)',
    'linear-gradient(135deg, #3b82f6, #06b6d4)',
    'linear-gradient(135deg, #10b981, #059669)',
    'linear-gradient(135deg, #f59e0b, #ef4444)',
    'linear-gradient(135deg, #ec4899, #f43f5e)',
    'linear-gradient(135deg, #14b8a6, #3b82f6)',
]

function getAvatarColor(nickname: string): string {
    const hash = nickname.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
    return AVATAR_COLORS[hash % AVATAR_COLORS.length]
}

const TABS = [
    // { key: 'broadcast', label: 'Broadcast Session', icon: 'broadcast', disabled: true },
    { key: 'chatbot', label: 'Chatbot', icon: 'chatbot', disabled: false },
    { key: 'liveAgent', label: 'LiveAgent', icon: 'live-agent', disabled: false },
    // { key: 'manualing', label: 'Manualing Session', icon: 'manualing', disabled: false },
    { key: 'closed', label: 'Closed', icon: 'closed', disabled: false },
]

type TabKey = 'broadcast' | 'chatbot' | 'liveAgent' | 'manualing' | 'closed'

const AGENT_STATUS_OPTIONS: { key: AgentStatus; label: string; color: string }[] = [
    { key: 'online', label: 'Online', color: '#22C55E' },
    { key: 'away', label: 'Away', color: '#F59E0B' },
]

const TAB_TYPE_MAP: Record<TabKey, 'closed' | 'bot' | 'manualing' | 'my_chat' | 'all_chat'> = {
    broadcast: 'all_chat',
    chatbot: 'bot',
    liveAgent: 'my_chat',
    manualing: 'manualing',
    closed: 'closed',
}

const PAGE_SIZE = 5

const MESSAGE_DELETE_WINDOW_MS = 5 * 60 * 1000

// 一次最多可发送的图片数量
const MAX_SEND_IMAGES = 9

// 自包含的小转圈（SVG + SMIL 旋转），尺寸精确、不依赖 antd Spin 的样式
const Spinner = ({ size = 14, color = '#25D1F7' }: { size?: number; color?: string }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 50 50"
        style={{ display: 'inline-block', verticalAlign: 'middle' }}
    >
        <circle
            cx="25"
            cy="25"
            r="20"
            fill="none"
            stroke={color}
            strokeWidth="5"
            strokeLinecap="round"
            strokeDasharray="90 60"
        >
            <animateTransform
                attributeName="transform"
                type="rotate"
                from="0 25 25"
                to="360 25 25"
                dur="0.8s"
                repeatCount="indefinite"
            />
        </circle>
    </svg>
)

const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
    'mousemove',
    'mousedown',
    'keydown',
    'wheel',
    'touchstart',
    'click',
]

const SENDER_TYPE_MAP: Record<string, 'user' | 'robot' | 'agent'> = {
    user: 'user',
    robot: 'robot',
    agent: 'agent',
}

const STATUS_TAB_MAP: Record<string, TabKey[]> = {
    bot: ['chatbot'],
    my_chat: ['liveAgent'],
    agent: ['liveAgent'],
    closed: ['closed'],
}

const LiveChat = () => {
    const companyName = useSelector((state: any) => state.user.userInfo?.company_name)
    const userName = useSelector((state: any) => state.user.userInfo?.user_name)
    const menus: string[] = useSelector((state: any) => state.user.userInfo?.menus ?? [])
    const hasWebSocket = menus.includes('websocket')
    const hasChatManage = menus.includes('chat_manage')

    const [channelList, setChannelList] = useState<ChannelItemRes[]>([])
    const [roomList, setRoomList] = useState<RoomItemRes[]>([])
    const [selectedChannels, setSelectedChannels] = useState<string[][]>([])
    const [activeTab, setActiveTab] = useState<TabKey>('liveAgent')
    const [liveAgentFilter, setLiveAgentFilter] = useState<'my_chat' | 'all_chat'>(
        hasWebSocket ? 'my_chat' : 'all_chat'
    )
    const [closedFilter, setClosedFilter] = useState<'my_chat_closed' | 'closed'>('my_chat_closed')
    const [closedMineCount, setClosedMineCount] = useState<number>(0)
    const [closedAllCount, setClosedAllCount] = useState<number>(0)
    useEffect(() => {
        if (hasWebSocket) {
            setLiveAgentFilter('my_chat')
        }
    }, [hasWebSocket])
    const [searchKeyword, setSearchKeyword] = useState('')
    const [searchType, setSearchType] = useState<'name' | 'tag'>('name')
    const [activeRoomId, setActiveRoomId] = useState<number | null>(null)
    const [roomDetail, setRoomDetail] = useState<RoomDetailData | null>(null)
    const [detailLoading, setDetailLoading] = useState(false)
    const [inputValue, setInputValue] = useState('')
    const [sending, setSending] = useState(false)
    const [previewImage, setPreviewImage] = useState<string | null>(null)
    const [sendProgress, setSendProgress] = useState<{ current: number; total: number } | null>(null)
    const [agentList, setAgentList] = useState<string[]>([])
    const [agentListLoading, setAgentListLoading] = useState(false)
    const [transferPanelVisible, setTransferPanelVisible] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const [hasMore, setHasMore] = useState(true)
    const [hasContent, setHasContent] = useState(false)
    const [roomListLoading, setRoomListLoading] = useState(false)
    const [myChatCount, setMyChatCount] = useState(0)
    const [liveAgentAllCount, setLiveAgentAllCount] = useState(0)
    const [tagInputValue, setTagInputValue] = useState('')
    const [isIdle, setIsIdle] = useState(false)
    const [agentStatus, setAgentStatus] = useState<AgentStatus>('online')
    const [statusDropdownOpen, setStatusDropdownOpen] = useState(false)
    const [statusUpdating, setStatusUpdating] = useState(false)
    const statusControlRef = useRef<HTMLDivElement>(null)
    const [idleTimeoutMs, setIdleTimeoutMs] = useState<number | null>(null)
    const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const lastActivityAtRef = useRef<number>(Date.now())
    const shouldSwitchToLiveAgentRef = useRef(false)
    const isLiveChatPageActiveRef = useRef(true)
    const [tagSaving, setTagSaving] = useState(false)
    const [nowTick, setNowTick] = useState(Date.now())
    const editableRef = useRef<HTMLDivElement>(null)
    const messagesEndRef = useRef<HTMLDivElement>(null)
    const messageListRef = useRef<HTMLDivElement>(null)
    const prevFirstMsgIdRef = useRef<number | null>(null)
    const loadingMoreRef = useRef(false)
    const autoLoadedOnceRef = useRef(false)
    const fetchRoomListTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const msgIdCounterRef = useRef(0)

    const wsUrl = useMemo(() => {
        if (isIdle) return null
        if (!hasWebSocket) return null
        if (!companyName || !userName) return null
        const params = new URLSearchParams({
            company: companyName,
            agent_id: userName,
        })
        const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
        return `${protocol}://chatbot.samagent.ai/ws/agent/livechat?${params.toString()}`
    }, [isIdle, hasWebSocket, companyName, userName])

    useEffect(() => {
        if (!companyName) return
        getChannelListApi({ company: companyName }).then((res) => {
            if (res.code === 0) {
                setChannelList(res.data.list)
            }
        })
    }, [companyName])

    const fetchRoomList = useCallback((resetSelection = true) => {
        if (!companyName) return
        if (!userName) return
        if (resetSelection) {
            setRoomListLoading(true)
        }
        const channelIds = selectedChannels.map((item) => item[1])
        getRoomListApi({
            company: companyName,
            agent_id: userName,
            type: activeTab === 'liveAgent' ? liveAgentFilter : activeTab === 'closed' ? closedFilter : TAB_TYPE_MAP[activeTab],
            channel: '',
            channel_id: channelIds,
        }).then((res) => {
            if (res.code === 0) {
                setRoomList(res.data)
                if (activeTab === 'closed') {
                    if (closedFilter === 'my_chat_closed') {
                        setClosedMineCount(res.data.length)
                    } else {
                        setClosedAllCount(res.data.length)
                    }
                }
                if (activeTab === 'liveAgent') {
                    if (liveAgentFilter === 'my_chat') {
                        setMyChatCount(res.data.length)
                    } else {
                        setLiveAgentAllCount(res.data.length)
                    }
                }
                if (resetSelection) {
                    setActiveRoomId(null)
                    setRoomDetail(null)
                }
            }
        }).finally(() => {
            setRoomListLoading(false)
        })
    }, [companyName, userName, activeTab, liveAgentFilter, closedFilter, selectedChannels])

    const fetchClosedOtherCount = useCallback(() => {
        if (!companyName || !userName || activeTab !== 'closed') return
        const channelIds = selectedChannels.map((item) => item[1])
        const otherType = closedFilter === 'my_chat_closed' ? 'closed' : 'my_chat_closed'
        getRoomListApi({
            company: companyName,
            agent_id: userName,
            type: otherType,
            channel: '',
            channel_id: channelIds,
        }).then((res) => {
            if (res.code === 0) {
                if (otherType === 'closed') {
                    setClosedAllCount(res.data.length)
                } else {
                    setClosedMineCount(res.data.length)
                }
            }
        })
    }, [companyName, userName, activeTab, closedFilter, selectedChannels])

    useEffect(() => {
        fetchClosedOtherCount()
    }, [fetchClosedOtherCount])

    const fetchLiveAgentOtherCount = useCallback(() => {
        if (!companyName || !userName || activeTab !== 'liveAgent') return
        const channelIds = selectedChannels.map((item) => item[1])
        const otherType = liveAgentFilter === 'my_chat' ? 'all_chat' : 'my_chat'
        getRoomListApi({
            company: companyName,
            agent_id: userName,
            type: otherType,
            channel: '',
            channel_id: channelIds,
        }).then((res) => {
            if (res.code === 0) {
                if (otherType === 'all_chat') {
                    setLiveAgentAllCount(res.data.length)
                } else {
                    setMyChatCount(res.data.length)
                }
            }
        })
    }, [companyName, userName, activeTab, liveAgentFilter, selectedChannels])

    useEffect(() => {
        fetchLiveAgentOtherCount()
    }, [fetchLiveAgentOtherCount])

    useEffect(() => {
        fetchRoomList(true)
    }, [fetchRoomList])

    const fetchMyChatStatus = useCallback(() => {
        if (!companyName || !userName) return
        const channelIds = selectedChannels.map((item) => item[1])
        getRoomListApi({
            company: companyName,
            agent_id: userName,
            type: 'my_chat',
            channel: '',
            channel_id: channelIds,
        }).then((res) => {
            if (res.code === 0) {
                setMyChatCount(res.data?.length ?? 0)
            }
        })
    }, [companyName, userName, selectedChannels])

    useEffect(() => {
        if (!hasWebSocket) return
        if (activeTab === 'liveAgent' && liveAgentFilter === 'my_chat') return
        fetchMyChatStatus()
    }, [fetchMyChatStatus, activeTab, liveAgentFilter, hasWebSocket])

    const debouncedFetchRoomList = useCallback(() => {
        if (fetchRoomListTimerRef.current) {
            clearTimeout(fetchRoomListTimerRef.current)
        }
        fetchRoomListTimerRef.current = setTimeout(() => {
            fetchRoomList(false)
            fetchRoomListTimerRef.current = null
        }, 500)
    }, [fetchRoomList])

    const refreshCurrentRoomDetail = useCallback((roomId: number) => {
        const room = roomList.find((r) => r.room_id === roomId)
        if (!room) return
        getRoomDetailApi({
            room_id: room.room_id,
            session_id: room.session_id,
            data: { last_msg_id: 0, size: PAGE_SIZE },
        }).then((res) => {
            if (res.code === 0) {
                setRoomDetail((prev) => {
                    if (!prev || prev.room_id !== roomId) return prev
                    const latestMessages = res.data.messages
                    const latestMsgIds = new Set(
                        latestMessages.map(m => m.message?.[0]?.msg_id).filter(Boolean)
                    )
                    const filteredPrevMessages = prev.messages.filter(m => {
                        if (m.id < 0 && m.message?.[0]?.msg_id && latestMsgIds.has(m.message[0].msg_id)) {
                            return false
                        }
                        return true
                    })
                    const existingIds = new Set(filteredPrevMessages.map(m => m.id))
                    const newMsgs = latestMessages.filter(m => !existingIds.has(m.id))
                    const updatedIds = new Set(latestMessages.map(m => m.id))
                    const mergedMessages = [
                        ...newMsgs,
                        ...filteredPrevMessages.map(m =>
                            updatedIds.has(m.id)
                                ? latestMessages.find(lm => lm.id === m.id)!
                                : m
                        ),
                    ]
                    return { ...res.data, messages: mergedMessages }
                })
            }
        })
    }, [roomList])

    const updateRoomListItem = useCallback((targetRoomId: number | undefined, wsMessage: any, senderType: string) => {
        if (targetRoomId == null) {
            debouncedFetchRoomList()
            return
        }

        setRoomList((prevList) => {
            const idx = prevList.findIndex((r) => r.room_id === targetRoomId)
            if (idx === -1) {
                debouncedFetchRoomList()
                return prevList
            }

            const updated = { ...prevList[idx] }

            if (wsMessage) {
                updated.message = {
                    id: 0,
                    send_type: SENDER_TYPE_MAP[senderType] || 'user',
                    send_time: Math.floor(Date.now() / 1000),
                    status: 'delivered',
                    message: [{
                        msg_id: wsMessage.msg_id || '',
                        message: {
                            type: wsMessage.message?.type || 'text',
                            text: wsMessage.message?.text || '',
                        },
                        custom: wsMessage.custom,
                    }],
                }
            }

            if (targetRoomId !== activeRoomId) {
                updated.has_unread = true
            }

            const newList = [...prevList]
            newList.splice(idx, 1)
            newList.unshift(updated)
            return newList
        })
    }, [activeRoomId, debouncedFetchRoomList])

    const handleWsMessage = useCallback((data: any) => {
        console.log('[WebWidget WS] message:', data)
        const event = data?.event
        const wsRoom = data?.room || {}
        const wsMessage = data?.message
        const senderType = data?.sender_type || ''

        if (event === 'connect.start') {
            const keepLiveTime = Number(data?.connect?.keep_live_time ?? 0)
            setIdleTimeoutMs(keepLiveTime > 0 ? keepLiveTime * 60 * 1000 : 0)
            return
        }

        const isMyChat = activeTab === 'liveAgent' && liveAgentFilter === 'my_chat'
        const targetRoomId: number | undefined = wsRoom.id
        const isCurrentRoom = targetRoomId != null && targetRoomId === activeRoomId

        // ========== 场景1：坐席在 liveAgent session my_chat tab ==========
        if (isMyChat) {
            if (event === 'message.send') return

            if (wsRoom.agent_id !== userName) return

            if (event === 'session.assign' || event === 'session.hand_over') {
                if (isCurrentRoom) {
                    refreshCurrentRoomDetail(targetRoomId)
                }
                debouncedFetchRoomList()
                return
            }

            if (event === 'session.close') {
                if (isCurrentRoom) {
                    setActiveRoomId(null)
                    setRoomDetail(null)
                }
                debouncedFetchRoomList()
                return
            }

            if (event === 'message.status') {
                if (isCurrentRoom) {
                    refreshCurrentRoomDetail(targetRoomId)
                }
                debouncedFetchRoomList()
                return
            }

            if (event === 'session.tag_add') {
                return
            }

            if (event === 'message.received') {
                if (isCurrentRoom && wsMessage) {
                    const incomingMsgId = wsMessage.msg_id || ''
                    const sendType = SENDER_TYPE_MAP[senderType] || 'user'
                    const incomingMsg: RoomDetailMessage = {
                        id: --msgIdCounterRef.current,
                        message: [{
                            msg_id: incomingMsgId,
                            message: {
                                type: wsMessage.message?.type || 'text',
                                text: wsMessage.message?.text || '',
                                media: wsMessage.message?.media,
                            },
                            custom: wsMessage.custom,
                        }],
                        send_time: Math.floor(Date.now() / 1000),
                        send_type: sendType,
                        status: 'received',
                    }
                    setRoomDetail((prev) => {
                        if (!prev || prev.room_id !== targetRoomId) return prev
                        if (incomingMsgId && prev.messages.some(m => m.message?.[0]?.msg_id === incomingMsgId)) {
                            return prev
                        }
                        return { ...prev, messages: [incomingMsg, ...prev.messages] }
                    })
                }

                updateRoomListItem(targetRoomId, wsMessage, senderType)
                return
            }

            return
        }

        // ========== 场景2：坐席不在 liveAgent session my_chat tab ==========
        if (event === 'session.close') {
            if (isCurrentRoom) {
                setActiveRoomId(null)
                setRoomDetail(null)
            }
            debouncedFetchRoomList()
            return
        }

        if (event === 'session.assign') {
            if (isCurrentRoom) {
                if (activeTab === 'chatbot') {
                    setActiveRoomId(null)
                    setRoomDetail(null)
                } else {
                    refreshCurrentRoomDetail(targetRoomId)
                }
            }
            debouncedFetchRoomList()
            fetchMyChatStatus()
            return
        }

        if (event === 'session.hand_over') {
            if (isCurrentRoom) {
                refreshCurrentRoomDetail(targetRoomId)
            }
            fetchMyChatStatus()
            return
        }

        if (event === 'session.tag_add') {
            if (activeTab === 'liveAgent' && liveAgentFilter === 'all_chat') {
                debouncedFetchRoomList()
            }
            return
        }

        if (event !== 'message.send' && event !== 'message.received') return

        const currentTabType = activeTab === 'liveAgent' ? liveAgentFilter : TAB_TYPE_MAP[activeTab]
        const matchTabs = STATUS_TAB_MAP[wsRoom.status] || []
        const statusMatch = matchTabs.includes(activeTab) ||
            (activeTab === 'liveAgent' && liveAgentFilter === 'all_chat' && (wsRoom.status === 'my_chat' || wsRoom.status === 'agent')) ||
            wsRoom.status === currentTabType

        if (!statusMatch) return

        if (isCurrentRoom && wsMessage) {
            const incomingMsgId = wsMessage.msg_id || ''
            const sendType = SENDER_TYPE_MAP[senderType] || (event === 'message.send' ? 'robot' : 'user')
            const incomingMsg: RoomDetailMessage = {
                id: --msgIdCounterRef.current,
                message: [{
                    msg_id: incomingMsgId,
                    message: {
                        type: wsMessage.message?.type || 'text',
                        text: wsMessage.message?.text || '',
                        media: wsMessage.message?.media,
                    },
                    custom: wsMessage.custom,
                }],
                send_time: Math.floor(Date.now() / 1000),
                send_type: sendType,
                status: event === 'message.send' ? 'send' : 'received',
            }
            setRoomDetail((prev) => {
                if (!prev || prev.room_id !== targetRoomId) return prev
                if (incomingMsgId && prev.messages.some(m => m.message?.[0]?.msg_id === incomingMsgId)) {
                    return prev
                }
                return { ...prev, messages: [incomingMsg, ...prev.messages] }
            })
        }

        updateRoomListItem(targetRoomId, wsMessage, senderType)
        fetchMyChatStatus()
    }, [activeTab, liveAgentFilter, activeRoomId, userName, debouncedFetchRoomList, refreshCurrentRoomDetail, updateRoomListItem, fetchMyChatStatus])

    const { disconnect } = useWebSocket({
        url: wsUrl,
        onMessage: handleWsMessage,
        onOpen: () => {
            console.log('[WebWidget WS] connected')
            // 连接建立即视为在线
            setAgentStatus('online')
            if (shouldSwitchToLiveAgentRef.current) {
                shouldSwitchToLiveAgentRef.current = false
                setActiveTab('liveAgent')
                setLiveAgentFilter('my_chat')
            }
        },
        onClose: (event) => {
            if (event.code !== 1000) {
                console.warn(`[WebWidget WS] 异常关闭: ${event.code} - ${event.reason || '未知原因'}`)
            }
        },
        onError: () => console.log('[WebWidget WS] error'),
    })

    useUnactivate(() => {
        isLiveChatPageActiveRef.current = false
    })

    useActivate(() => {
        isLiveChatPageActiveRef.current = true
    })

    useEffect(() => {
        if (!hasWebSocket) return
        return onLogout(() => {
            setIsIdle(true)
            disconnect()
        })
    }, [hasWebSocket, disconnect])

    useEffect(() => {
        if (!hasWebSocket) return
        if (isIdle) return
        if (idleTimeoutMs === null) return
        if (idleTimeoutMs === 0) return

        const scheduleIdleTimer = () => {
            if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
            idleTimerRef.current = setTimeout(() => {
                setIsIdle(true)
            }, idleTimeoutMs)
        }

        const handleActivity = () => {
            if (!isLiveChatPageActiveRef.current) return
            const now = Date.now()
            if (now - lastActivityAtRef.current < 1000) return
            lastActivityAtRef.current = now
            scheduleIdleTimer()
        }

        ACTIVITY_EVENTS.forEach((ev) => {
            window.addEventListener(ev, handleActivity, { passive: true })
        })

        scheduleIdleTimer()

        return () => {
            ACTIVITY_EVENTS.forEach((ev) => {
                window.removeEventListener(ev, handleActivity)
            })
            if (idleTimerRef.current) {
                clearTimeout(idleTimerRef.current)
                idleTimerRef.current = null
            }
        }
    }, [hasWebSocket, isIdle, idleTimeoutMs])

    useEffect(() => {
        return () => {
            if (fetchRoomListTimerRef.current) {
                clearTimeout(fetchRoomListTimerRef.current)
            }
        }
    }, [])

    useEffect(() => {
        const timer = setInterval(() => setNowTick(Date.now()), 30 * 1000)
        return () => clearInterval(timer)
    }, [])

    useEffect(() => {
        if (isIdle && activeTab === 'liveAgent') {
            setActiveTab('chatbot')
            setLiveAgentFilter(hasWebSocket ? 'my_chat' : 'all_chat')
        }
    }, [isIdle, activeTab, hasWebSocket])

    const activeRoom = useMemo(
        () => roomList.find((r) => r.room_id === activeRoomId),
        [roomList, activeRoomId]
    )

    useEffect(() => {
        setTagInputValue(activeRoom?.tag || '')
    }, [activeRoom?.tag, activeRoomId])

    const handleRoomClick = useCallback((room: RoomItemRes) => {
        if (activeRoomId === room.room_id) return
        setActiveRoomId(room.room_id)
        setRoomDetail(null)
        setDetailLoading(true)
        setHasMore(true)
        prevFirstMsgIdRef.current = null
        autoLoadedOnceRef.current = false
        getRoomDetailApi({
            room_id: room.room_id,
            session_id: room.session_id,
            data: { last_msg_id: 0, size: PAGE_SIZE },
        })
            .then((res) => {
                if (res.code === 0) {
                    setRoomDetail(res.data)
                    setHasMore(res.data.messages.length >= PAGE_SIZE)
                    fetchRoomList(false)
                }
            })
            .finally(() => setDetailLoading(false))
    }, [activeRoomId, fetchRoomList])

    useEffect(() => {
        if (!roomDetail?.messages.length) {
            prevFirstMsgIdRef.current = null
            return
        }
        const currentFirstMsgId = roomDetail.messages[0]?.id || null
        const isNewMessage = prevFirstMsgIdRef.current !== null && currentFirstMsgId !== prevFirstMsgIdRef.current
        const isInitialLoad = prevFirstMsgIdRef.current === null

        if (isInitialLoad || isNewMessage) {
            messagesEndRef.current?.scrollIntoView({ behavior: isInitialLoad ? 'auto' : 'smooth' })
        }
        prevFirstMsgIdRef.current = currentFirstMsgId
    }, [roomDetail?.messages])

    const loadMoreMessages = useCallback(async () => {
        if (!roomDetail || !activeRoom || loadingMoreRef.current || !hasMore) return

        const messages = roomDetail.messages
        if (messages.length === 0) return

        const oldestMsg = messages[messages.length - 1]
        const lastMsgId = oldestMsg.id
        if (!lastMsgId) return

        loadingMoreRef.current = true
        setLoadingMore(true)

        const listEl = messageListRef.current
        const prevScrollHeight = listEl?.scrollHeight || 0

        try {
            const res = await getRoomDetailApi({
                room_id: activeRoom.room_id,
                session_id: activeRoom.session_id,
                data: { last_msg_id: lastMsgId, size: PAGE_SIZE },
            })
            if (res.code === 0) {
                const olderMessages = res.data.messages
                if (olderMessages.length < PAGE_SIZE) {
                    setHasMore(false)
                }
                if (olderMessages.length > 0) {
                    setRoomDetail((prev) => {
                        if (!prev) return prev
                        const existingIds = new Set(prev.messages.map(m => m.id))
                        const newMessages = olderMessages.filter(m => !existingIds.has(m.id))
                        if (newMessages.length === 0) return prev
                        return { ...prev, messages: [...prev.messages, ...newMessages] }
                    })
                    requestAnimationFrame(() => {
                        if (listEl) {
                            const newScrollHeight = listEl.scrollHeight
                            listEl.scrollTop = newScrollHeight - prevScrollHeight
                        }
                    })
                }
            }
        } finally {
            loadingMoreRef.current = false
            setLoadingMore(false)
        }
    }, [roomDetail, activeRoom, hasMore])

    useEffect(() => {
        const listEl = messageListRef.current
        if (!listEl) return

        const handleScroll = () => {
            if (listEl.scrollTop <= 50 && hasMore && !loadingMoreRef.current) {
                loadMoreMessages()
            }
        }

        const handleWheel = (e: WheelEvent) => {
            if (e.deltaY >= 0 || !hasMore || loadingMoreRef.current) return
            const contentNotOverflowing = listEl.scrollHeight <= listEl.clientHeight
            const scrolledToTop = listEl.scrollTop <= 50
            if (contentNotOverflowing || scrolledToTop) {
                loadMoreMessages()
            }
        }

        listEl.addEventListener('scroll', handleScroll)
        listEl.addEventListener('wheel', handleWheel)
        return () => {
            listEl.removeEventListener('scroll', handleScroll)
            listEl.removeEventListener('wheel', handleWheel)
        }
    }, [hasMore, loadMoreMessages])

    useEffect(() => {
        if (autoLoadedOnceRef.current) return
        const listEl = messageListRef.current
        if (!listEl || !roomDetail?.messages.length || loadingMore || !hasMore) return

        const contentNotOverflowing = listEl.scrollHeight <= listEl.clientHeight
        if (contentNotOverflowing) {
            autoLoadedOnceRef.current = true
            loadMoreMessages()
        }
    }, [roomDetail?.messages, loadingMore, hasMore, loadMoreMessages])

    const renderMessageText = (msg: RoomDetailMessage) => {
        const innerMsgs = msg.message
        if (!innerMsgs?.length) return null
        const firstMsg = innerMsgs[0]
        if (firstMsg.message.type === 'system') {
            const systemInfo = firstMsg.message.system
            const systemTextMap: Record<string, string> = {
                'conversation.start': 'Session begins',
                'conversation.close': 'Session closed',
                'conversation.take_over': `Session taken over by ${systemInfo?.to || ''}`,
                'conversation.transfer': `Session transferred to ${systemInfo?.to || ''}`,
                'conversation.hand_over': `Session transferred to ${systemInfo?.to || ''}`,
            }
            const displayText = systemTextMap[systemInfo?.type || ''] ?? systemInfo?.type ?? 'System event'
            return (
                <div className={styles.systemMessage}>{displayText}</div>
            )
        }
        if (firstMsg.message.type === 'image') {
            const url = firstMsg.message.media?.origin_url || firstMsg.message.text || ''
            return url ? (
                <Image
                    src={url}
                    alt="image"
                    width={200}
                    style={{ borderRadius: 8, cursor: 'zoom-in' }}
                    preview={{ mask: false }}
                />
            ) : (
                <span>[Image]</span>
            )
        }
        if (firstMsg.message.type === 'video') {
            const url = firstMsg.message.media?.origin_url || ''
            if (!url) return <span>[Video]</span>
            const isAudio = /\.(mp3|wav|ogg|aac|m4a|flac|wma|amr|opus)(\?.*)?$/i.test(url)
            if (isAudio) {
                return <audio src={url} controls style={{ maxWidth: 240 }} />
            }
            return <video src={url} controls style={{ maxWidth: 320, borderRadius: 8 }} />
        }
        if (firstMsg.message.type === 'audio') {
            const url = firstMsg.message.media?.origin_url || ''
            const text = firstMsg.message.text || ''
            if (!url && !text) return <span>[Audio]</span>
            return (
                <div>
                    {url && <audio src={url} controls style={{ maxWidth: 240 }} />}
                    {text && <div style={{ marginTop: 4, fontSize: 13, color: '#ccc' }}>{text}</div>}
                </div>
            )
        }
        if (firstMsg.message.type === 'file') {
            const url = firstMsg.message.media?.origin_url || ''
            if (!url) return <span>[File]</span>
            const fileName = decodeURIComponent(url.split('/').pop() || 'File')
            return (
                <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '6px 10px',
                        background: 'rgba(255,255,255,0.06)',
                        borderRadius: 8,
                        color: '#25D1F7',
                        textDecoration: 'none',
                        fontSize: 13,
                        wordBreak: 'break-all',
                    }}
                >
                    <img src={fileIcon} alt="file" width={18} height={18} style={{ flexShrink: 0 }} />
                    {fileName}
                </a>
            )
        }
        return <span>{firstMsg.message.text || ''}</span>
    }

    const filteredRoomList = useMemo(() => {
        const kw = searchKeyword.trim().toLowerCase()
        if (!kw) return roomList
        if (searchType === 'name') {
            return roomList.filter((room) => room.nickname?.toLowerCase().includes(kw))
        }
        return roomList.filter((room) => room.tag?.toLowerCase().includes(kw))
    }, [roomList, searchKeyword, searchType])

    const groupedByTag = useMemo(() => {
        if (liveAgentFilter !== 'all_chat') return null
        const grouped: Record<string, RoomItemRes[]> = {}
        const noTag: RoomItemRes[] = []
        filteredRoomList.forEach((room) => {
            if (room.tag) {
                if (!grouped[room.tag]) grouped[room.tag] = []
                grouped[room.tag].push(room)
            } else {
                noTag.push(room)
            }
        })
        const sortedKeys = Object.keys(grouped).sort()
        const result: { tag: string; rooms: RoomItemRes[] }[] = sortedKeys.map((tag) => ({
            tag,
            rooms: grouped[tag],
        }))
        if (noTag.length > 0) {
            result.push({ tag: '', rooms: noTag })
        }
        return result
    }, [filteredRoomList, liveAgentFilter])

    const renderRoomItem = (room: RoomItemRes) => (
        <div
            key={room.call_id}
            className={classNames(styles.roomItem, {
                [styles.unread]: room.has_unread,
                [styles.activeRoom]: activeRoomId === room.room_id,
            })}
            onClick={() => handleRoomClick(room)}
        >
            <div className={styles.roomAvatar}>
                {room.avatar ? (
                    <img src={room.avatar} alt="" className={styles.avatarImg} />
                ) : (
                    <div
                        className={styles.avatarPlaceholder}
                        style={{ background: getAvatarColor(room.nickname || '') }}
                    >
                        {room.nickname
                            ?.split(' ')
                            .map(word => word.charAt(0))
                            .join('')
                            .slice(0, 2)
                            .toUpperCase() || '?'}
                    </div>
                )}
                <img
                    src={typeIconMap[room.channel]}
                    alt={room.channel}
                    className={styles.channelBadge}
                />
            </div>
            <div className={styles.roomInfo}>
                <div className={styles.roomNameRow}>
                    <Tooltip title={room.nickname} placement="topLeft">
                        <span className={styles.roomNickname}>{room.nickname}</span>
                    </Tooltip>
                    {room.tag && (
                        <Tooltip title={room.tag} placement="top">
                            <span className={styles.roomTag}>{room.tag}</span>
                        </Tooltip>
                    )}
                </div>
                <div className={styles.roomLastMsg}>
                    {room.message?.message?.[0]?.message?.text || ''}
                </div>
            </div>
            <div className={styles.roomRight}>
                <span className={styles.roomTime}>
                    {room.message?.send_time ? formatRoomTime(room.message.send_time) : ''}
                </span>
                {room.has_unread && (
                    <span className={styles.unreadDot} />
                )}
            </div>
        </div>
    )

    const handleSend = useCallback(async () => {
        if (!editableRef.current || !activeRoom || sending) return

        const el = editableRef.current

        type SendPayload =
            | { type: 'image'; media: { origin_url: string } }
            | { type: 'text'; text: string }

        // 方案A：按 DOM 顺序（含嵌套）收集文字与图片，保持它们在输入框里的原始先后顺序
        const messagesToSend: SendPayload[] = []
        let textBuf = ''
        const flushText = () => {
            const t = textBuf.trim()
            if (t) messagesToSend.push({ type: 'text', text: t })
            textBuf = ''
        }
        // 递归遍历，避免漏掉被浏览器包进块级元素里的嵌套图片/文字
        const walk = (node: Node) => {
            node.childNodes.forEach((child) => {
                if (child.nodeType === Node.TEXT_NODE) {
                    textBuf += child.textContent || ''
                } else if (child instanceof HTMLElement) {
                    if (child.tagName === 'IMG') {
                        const imgUrl = child.getAttribute('data-image-url')
                        if (imgUrl) {
                            flushText()
                            messagesToSend.push({ type: 'image', media: { origin_url: imgUrl } })
                        }
                    } else if (child.tagName === 'BR') {
                        textBuf += '\n'
                    } else {
                        // 块级元素视为换行边界，并递归其子节点
                        if (/^(DIV|P)$/.test(child.tagName) && textBuf && !textBuf.endsWith('\n')) {
                            textBuf += '\n'
                        }
                        walk(child)
                    }
                }
            })
        }
        walk(el)
        flushText()

        if (messagesToSend.length === 0) return

        // 数量上限：一次最多发送 MAX_SEND_IMAGES 张图片
        const imageCount = messagesToSend.filter((m) => m.type === 'image').length
        if (imageCount > MAX_SEND_IMAGES) {
            message.warning(`A maximum of ${MAX_SEND_IMAGES} images can be sent at once`)
            return
        }

        // 用剩余（未发送成功）的 payload 重建输入框内容，实现部分失败保留
        const rebuildEditable = (payloads: SendPayload[]) => {
            el.innerHTML = ''
            payloads.forEach((p) => {
                if (p.type === 'image') {
                    const img = document.createElement('img')
                    img.src = p.media.origin_url
                    img.className = styles.inlineImage
                    img.style.cursor = 'zoom-in'
                    img.setAttribute('data-image-url', p.media.origin_url)
                    el.appendChild(img)
                } else {
                    el.appendChild(document.createTextNode(p.text))
                }
            })
        }

        setSending(true)
        const total = messagesToSend.length
        let sentCount = 0
        let failed = false
        // 发送中进度（多条时显示 x/total），用于输入框上方与发送按钮的内联提示
        setSendProgress({ current: 1, total })
        try {
            for (const messagePayload of messagesToSend) {
                const res = await sendRoomMessageApi({
                    room_id: activeRoom.room_id,
                    session_id: activeRoom.session_id,
                    data: { message: messagePayload },
                })
                if (res.code !== 0) {
                    failed = true
                    break
                }
                sentCount++
                // 还有剩余则更新进度
                if (sentCount < total) setSendProgress({ current: sentCount + 1, total })
                setRoomDetail((prev) => {
                    if (!prev) return prev
                    const newMsg: RoomDetailMessage = {
                        id: --msgIdCounterRef.current,
                        message: [{
                            msg_id: res.data.msg_id,
                            message: messagePayload,
                        }],
                        send_time: Math.floor(Date.now() / 1000),
                        send_type: 'agent',
                        status: 'send',
                    }
                    return { ...prev, messages: [newMsg, ...prev.messages] }
                })
            }
        } catch {
            failed = true
        } finally {
            // 关闭发送中提示
            setSendProgress(null)
            // 保留未发送成功的部分；全部成功则清空
            const remaining = messagesToSend.slice(sentCount)
            rebuildEditable(remaining)
            setHasContent(remaining.length > 0)
            if (remaining.length === 0) setInputValue('')
            if (failed) message.error('Send failed')
            if (sentCount > 0) fetchRoomList(false)
            setSending(false)
            // 发送完成后，等 React 把 contentEditable 重新变回 true 再聚焦，
            // 并把光标放到输入框末尾，实现无需再次点击即可继续输入
            requestAnimationFrame(() => {
                const el2 = editableRef.current
                if (!el2) return
                el2.focus()
                const range = document.createRange()
                range.selectNodeContents(el2)
                range.collapse(false)
                const sel = window.getSelection()
                sel?.removeAllRanges()
                sel?.addRange(range)
            })
        }
    }, [activeRoom, sending, fetchRoomList])

    const handleTransferClick = useCallback(async () => {
        if (transferPanelVisible) {
            setTransferPanelVisible(false)
            return
        }
        if (!companyName || !userName) return
        setAgentListLoading(true)
        setTransferPanelVisible(true)
        try {
            const res = await getRoomAgentsApi({ company: companyName, agent_id: userName })
            if (res.code === 0) {
                setAgentList(res.data)
            }
        } finally {
            setAgentListLoading(false)
        }
    }, [transferPanelVisible, companyName, userName])

    const handleHandover = useCallback(async (targetAgent: string) => {
        if (!activeRoom || !userName) return
        // 主管在 all_chat 下代其他座席转接
        const isSupervisorHandover = liveAgentFilter === 'all_chat' && hasChatManage
        // 主管取被转接会话的座席，普通座席取自己
        const fromAgent = isSupervisorHandover ? roomDetail?.agent_id : userName
        if (!fromAgent) return
        // 主管操作必须带 company
        if (isSupervisorHandover && !companyName) return
        try {
            const res = await handoverApi({
                // 仅主管账号带上 company 和 agent_id，普通座席保持原逻辑
                ...(isSupervisorHandover ? { company: companyName, agent_id: userName } : {}),
                room_id: activeRoom.room_id,
                session_id: activeRoom.session_id,
                data: {
                    from: fromAgent,
                    to: targetAgent,
                    type: 'hand_over',
                },
            })
            if (res.code === 0) {
                message.success('Transfer successful')
                setTransferPanelVisible(false)
                fetchRoomList(true)
            } else {
                message.error(res.message || 'Transfer failed')
            }
        } catch {
            message.error('Transfer failed')
        }
    }, [activeRoom, companyName, userName, liveAgentFilter, hasChatManage, roomDetail?.agent_id, fetchRoomList])

    const handleCloseSession = useCallback(async () => {
        if (!activeRoom) return
        try {
            const res = await closeRoomApi({
                room_id: activeRoom.room_id,
                session_id: activeRoom.session_id,
            })
            if (res.code === 0) {
                message.success('Session closed')
                fetchRoomList(true)
            } else {
                message.error(res.message || 'Close failed')
            }
        } catch {
            message.error('Close failed')
        }
    }, [activeRoom, fetchRoomList])

    const uploadAndInsertImage = useCallback(async (file: File) => {
        if (!file || !roomDetail || !editableRef.current) return

        try {
            const res = await uploadFileApi({
                file,
                model: roomDetail.channel.toLowerCase() as UploadFileReq['model'],
                format: 'image',
                action: 'agent',
            })
            if (res.code === 0 && editableRef.current) {
                const img = document.createElement('img')
                img.src = res.data.filepath
                img.className = styles.inlineImage
                img.style.cursor = 'zoom-in'
                img.setAttribute('data-image-url', res.data.filepath)

                editableRef.current.focus()
                const selection = window.getSelection()
                // 仅当光标仍在输入框内时才在光标处插入，否则追加到末尾
                // （上传是异步的，await 期间焦点可能已经离开输入框）
                if (
                    selection &&
                    selection.rangeCount > 0 &&
                    editableRef.current.contains(selection.anchorNode)
                ) {
                    const range = selection.getRangeAt(0)
                    range.deleteContents()
                    range.insertNode(img)
                    range.setStartAfter(img)
                    range.collapse(true)
                    selection.removeAllRanges()
                    selection.addRange(range)
                } else {
                    editableRef.current.appendChild(img)
                }
                setHasContent(true)
            } else if (res.code !== 0) {
                message.error(res.message || 'Upload failed')
            }
        } catch {
            message.error('Upload failed')
        }
    }, [roomDetail])

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) await uploadAndInsertImage(file)
        e.target.value = ''
    }

    const handlePaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
        // 优先从剪贴板里找图片（截图、复制的图片文件等）
        const items = e.clipboardData?.items
        let imageFile: File | null = null
        if (items) {
            for (let i = 0; i < items.length; i++) {
                if (items[i].kind === 'file' && items[i].type.startsWith('image/')) {
                    imageFile = items[i].getAsFile()
                    if (imageFile) break
                }
            }
        }

        if (imageFile) {
            e.preventDefault()
            uploadAndInsertImage(imageFile)
            return
        }

        // 非图片：只粘贴纯文本，避免带入富文本样式
        e.preventDefault()
        const text = e.clipboardData.getData('text/plain')
        document.execCommand('insertText', false, text)
    }, [uploadAndInsertImage])

    const handleDocUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file || !roomDetail || !activeRoom) return

        setSending(true)
        try {
            const res = await uploadFileApi({
                file,
                model: roomDetail.channel.toLowerCase() as UploadFileReq['model'],
                format: 'file',
                action: 'agent',
            })
            if (res.code === 0) {
                const sendRes = await sendRoomMessageApi({
                    room_id: activeRoom.room_id,
                    session_id: activeRoom.session_id,
                    data: { message: { type: 'file', media: { origin_url: res.data.filepath } } },
                })
                if (sendRes.code === 0) {
                    setRoomDetail((prev) => {
                        if (!prev) return prev
                        const newMsg: RoomDetailMessage = {
                            id: --msgIdCounterRef.current,
                            message: [{
                                msg_id: sendRes.data.msg_id,
                                message: { type: 'file', media: { origin_url: res.data.filepath } },
                            }],
                            send_time: Math.floor(Date.now() / 1000),
                            send_type: 'agent',
                            status: 'send',
                        }
                        return { ...prev, messages: [newMsg, ...prev.messages] }
                    })
                    fetchRoomList(false)
                }
            } else {
                message.error(res.message || 'Upload failed')
            }
        } catch {
            message.error('Upload failed')
        } finally {
            setSending(false)
        }
        e.target.value = ''
    }

    const handleAudioUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file || !roomDetail || !activeRoom) return

        setSending(true)
        try {
            const res = await uploadFileApi({
                file,
                model: roomDetail.channel.toLowerCase() as UploadFileReq['model'],
                format: 'video',
                action: 'agent',
            })
            if (res.code === 0) {
                const sendRes = await sendRoomMessageApi({
                    room_id: activeRoom.room_id,
                    session_id: activeRoom.session_id,
                    data: { message: { type: 'video', media: { origin_url: res.data.filepath } } },
                })
                if (sendRes.code === 0) {
                    setRoomDetail((prev) => {
                        if (!prev) return prev
                        const newMsg: RoomDetailMessage = {
                            id: --msgIdCounterRef.current,
                            message: [{
                                msg_id: sendRes.data.msg_id,
                                message: { type: 'video', media: { origin_url: res.data.filepath } },
                            }],
                            send_time: Math.floor(Date.now() / 1000),
                            send_type: 'agent',
                            status: 'send',
                        }
                        return { ...prev, messages: [newMsg, ...prev.messages] }
                    })
                    fetchRoomList(false)
                }
            } else {
                message.error(res.message || 'Upload failed')
            }
        } catch {
            message.error('Upload failed')
        } finally {
            setSending(false)
        }
        e.target.value = ''
    }

    const handleCopy = useCallback((text: string) => {
        copyToClipboard(text)
    }, [])

    const handleReconnect = useCallback(() => {
        if (!hasWebSocket) return
        shouldSwitchToLiveAgentRef.current = true
        setIsIdle(false)
        setIdleTimeoutMs(null)
        lastActivityAtRef.current = Date.now()
    }, [hasWebSocket])

    const handleStatusChange = useCallback(async (next: AgentStatus) => {
        setStatusDropdownOpen(false)
        if (next === agentStatus || statusUpdating) return
        if (!companyName || !userName) return
        const prev = agentStatus
        setAgentStatus(next) // 乐观更新
        setStatusUpdating(true)
        try {
            const res = await updateAgentStatusApi({
                company: companyName,
                agent_id: userName,
                status: next,
            })
            if (res.code !== 0) {
                setAgentStatus(prev)
                message.error(res.message || 'Failed to update status')
            }
        } catch {
            setAgentStatus(prev)
            message.error('Failed to update status')
        } finally {
            setStatusUpdating(false)
        }
    }, [agentStatus, statusUpdating, companyName, userName])

    // 断开连接时收起状态下拉
    useEffect(() => {
        if (isIdle) setStatusDropdownOpen(false)
    }, [isIdle])

    // 点击外部关闭状态下拉
    useEffect(() => {
        if (!statusDropdownOpen) return
        const handleClickOutside = (e: MouseEvent) => {
            if (statusControlRef.current && !statusControlRef.current.contains(e.target as Node)) {
                setStatusDropdownOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [statusDropdownOpen])

    const handleSaveTag = useCallback(async () => {
        if (!activeRoom || tagSaving) return
        setTagSaving(true)
        try {
            const res = await addRoomTagApi({
                room_id: activeRoom.room_id,
                session_id: activeRoom.session_id,
                data: {
                    agent_id: userName,
                    tag: tagInputValue,
                },
            })
            if (res.code === 0) {
                message.success('Tag saved')
                fetchRoomList(false)
            } else {
                message.error(res.message || 'Save failed')
            }
        } catch {
            message.error('Save failed')
        } finally {
            setTagSaving(false)
        }
    }, [activeRoom, tagSaving, tagInputValue, userName, fetchRoomList])

    const handleDeleteMessage = useCallback(async (msg: RoomDetailMessage) => {
        if (!activeRoom || !roomDetail) return
        const msgId = msg.message?.[0]?.msg_id
        if (!msgId) {
            message.error('Missing message id')
            return
        }

        if (Date.now() - msg.send_time * 1000 > MESSAGE_DELETE_WINDOW_MS) {
            message.error('Only messages within 5 minutes can be deleted')
            return
        }

        const backup = roomDetail
        const wasLatest = backup.messages[0]?.message?.[0]?.msg_id === msgId

        setRoomDetail(prev => {
            if (!prev) return prev
            return {
                ...prev,
                messages: prev.messages.filter(
                    m => m.message?.[0]?.msg_id !== msgId
                ),
            }
        })

        try {
            const res = await deleteRoomMessageApi({
                room_id: activeRoom.room_id,
                session_id: activeRoom.session_id,
                data: { msg_id: msgId },
            })
            if (res.code !== 0) {
                setRoomDetail(backup)
                message.error(res.message || 'Delete failed')
                return
            }
            message.success('Deleted')
            if (wasLatest) fetchRoomList(false)
        } catch {
            setRoomDetail(backup)
            message.error('Delete failed')
        }
    }, [activeRoom, roomDetail, fetchRoomList])

    const formatRoomTime = (timestamp: number) => {
        const date = new Date(timestamp * 1000)
        const now = new Date()
        const isToday =
            date.getFullYear() === now.getFullYear() &&
            date.getMonth() === now.getMonth() &&
            date.getDate() === now.getDate()
        if (isToday) {
            return date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
        }
        const mm = String(date.getMonth() + 1).padStart(2, '0')
        const dd = String(date.getDate()).padStart(2, '0')
        const hh = String(date.getHours()).padStart(2, '0')
        const min = String(date.getMinutes()).padStart(2, '0')
        return `${mm}-${dd} ${hh}:${min}`
    }

    const cascaderOptions = useMemo(() => {
        return CHANNEL_TYPES.map((type) => {
            const children = channelList
                .filter((item) => item.channel === type)
                .map((item) => ({
                    label: item.channel_name,
                    value: item.channel_id,
                }))
            return {
                label: (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <img
                            src={typeIconMap[type]}
                            alt={type}
                            style={{ width: 18, height: 18 }}
                        />
                        {type}
                    </span>
                ),
                value: type,
                disabled: children.length === 0,
                children,
            }
        })
    }, [channelList])

    const tabs = useMemo(
        () =>
            TABS.map((tab) => ({
                ...tab,
                disabled: tab.disabled || (isIdle && tab.key === 'liveAgent'),
            })),
        [isIdle]
    )

    return (
        <div className={styles.container}>
            <div className={styles.topBar}>
                <Cascader
                    popupClassName="live-chat-cascader-dropdown"
                    options={cascaderOptions}
                    value={selectedChannels as any}
                    onChange={(value) => setSelectedChannels(value)}
                    multiple
                    maxTagCount="responsive"
                    placeholder="Select Channels"
                    style={{ width: 335 }}
                    tagRender={({ label, closable, onClose }) => {
                        const labels = Array.isArray(label) ? label : [label]
                        const leafLabel = String(labels[labels.length - 1] ?? '')
                        const channel = channelList.find(c => c.channel_name === leafLabel)
                        const icon = channel ? typeIconMap[channel.channel] : null
                        return (
                            <span
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    background: 'rgba(255, 255, 255, 0.08)',
                                    borderRadius: 4,
                                    padding: '2px 8px',
                                    margin: '2px 4px 2px 0',
                                    fontSize: 13,
                                    color: '#E5E8EF',
                                    lineHeight: '22px',
                                }}
                            >
                                {icon && <img src={icon} alt="" style={{ width: 14, height: 14 }} />}
                                <span>{label}</span>
                                {closable && (
                                    <span
                                        onClick={onClose}
                                        style={{ cursor: 'pointer', marginLeft: 2, color: '#717787', fontSize: 12 }}
                                    >
                                        ×
                                    </span>
                                )}
                            </span>
                        )
                    }}
                />

                {hasWebSocket && !isIdle && (() => {
                    const current = AGENT_STATUS_OPTIONS.find((o) => o.key === agentStatus) ?? AGENT_STATUS_OPTIONS[0]
                    return (
                        <div className={styles.statusControl} ref={statusControlRef}>
                            <button
                                type="button"
                                className={styles.statusPill}
                                disabled={statusUpdating}
                                onClick={() => setStatusDropdownOpen((v) => !v)}
                            >
                                <span className={styles.statusDot} style={{ background: current.color }} />
                                <span className={styles.statusText}>{current.label}</span>
                                <svg
                                    className={classNames(styles.statusCaret, {
                                        [styles.statusCaretOpen]: statusDropdownOpen,
                                    })}
                                    width="10"
                                    height="10"
                                    viewBox="0 0 12 12"
                                    fill="none"
                                >
                                    <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                            {statusDropdownOpen && (
                                <div className={styles.statusDropdown}>
                                    {AGENT_STATUS_OPTIONS.map((opt) => (
                                        <div
                                            key={opt.key}
                                            className={classNames(styles.statusOption, {
                                                [styles.statusOptionActive]: opt.key === agentStatus,
                                            })}
                                            onClick={() => handleStatusChange(opt.key)}
                                        >
                                            <span className={styles.statusDot} style={{ background: opt.color }} />
                                            <span>{opt.label}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )
                })()}
            </div>

            <div className={styles.headerContainer}>
                <div className={styles.tabGroup}>
                    {tabs.map((tab) => (
                        <div
                            key={tab.key}
                            className={classNames(styles.tabItem, {
                                [styles.active]: activeTab === tab.key,
                                [styles.disabled]: tab.disabled,
                            })}
                            onClick={() => {
                                if (tab.disabled) return
                                setActiveTab(tab.key as TabKey)
                                if (tab.key !== 'liveAgent') {
                                    setLiveAgentFilter(hasWebSocket ? 'my_chat' : 'all_chat')
                                }
                                if (tab.key !== 'closed') {
                                    setClosedFilter('my_chat_closed')
                                }
                            }}
                        >
                            <SvgIcon name={tab.icon} width="16" height="16" />
                            {tab.label}
                            {tab.key === 'liveAgent' && myChatCount > 0 && activeTab !== 'liveAgent' && (
                                <span className={styles.tabBadge}>{myChatCount}</span>
                            )}
                        </div>
                    ))}
                </div>

                {isIdle && (
                    <div className={styles.idleBanner}>
                        <span className={styles.idleBannerText}>
                            Live Agent has been disconnected!
                        </span>
                        <button
                            className={styles.idleBannerBtn}
                            onClick={handleReconnect}
                        >
                            Connect
                        </button>
                    </div>
                )}
            </div>

            <div className={styles.contentArea}>
                <div className={styles.leftPanel}>
                    {activeTab === 'liveAgent' && hasWebSocket && (
                        <div className={styles.filterBtnGroup}>
                            <button
                                className={classNames(styles.filterBtn, {
                                    [styles.filterBtnActive]: liveAgentFilter === 'my_chat',
                                })}
                                onClick={() => setLiveAgentFilter('my_chat')}
                            >
                                Mine
                                {myChatCount > 0 && (
                                    <span className={styles.filterBadge}>{myChatCount}</span>
                                )}
                            </button>
                            <button
                                className={classNames(styles.filterBtn, {
                                    [styles.filterBtnActive]: liveAgentFilter === 'all_chat',
                                })}
                                onClick={() => setLiveAgentFilter('all_chat')}
                            >
                                All
                                {liveAgentAllCount > 0 && (
                                    <span className={styles.filterBadge}>{liveAgentAllCount}</span>
                                )}
                            </button>
                        </div>
                    )}
                    {activeTab === 'closed' && (
                        <div className={styles.filterBtnGroup}>
                            <button
                                className={classNames(styles.filterBtn, {
                                    [styles.filterBtnActive]: closedFilter === 'my_chat_closed',
                                })}
                                onClick={() => setClosedFilter('my_chat_closed')}
                            >
                                Mine
                                {closedMineCount > 0 && (
                                    <span className={styles.filterBadge}>{closedMineCount}</span>
                                )}
                            </button>
                            <button
                                className={classNames(styles.filterBtn, {
                                    [styles.filterBtnActive]: closedFilter === 'closed',
                                })}
                                onClick={() => setClosedFilter('closed')}
                            >
                                All
                                {closedAllCount > 0 && (
                                    <span className={styles.filterBadge}>{closedAllCount}</span>
                                )}
                            </button>
                        </div>
                    )}
                    <div className={styles.searchBar}>
                        <div className={styles.searchInputWrap}>
                            <img src={searchIcon} alt="search" className={styles.searchIconImg} />
                            <input
                                className={styles.searchInput}
                                value={searchKeyword}
                                onChange={(e) => setSearchKeyword(e.target.value)}
                                placeholder={searchType === 'name' ? 'Search Name/Tag' : 'Search Tag/Name'}
                            />
                        </div>
                        <div className={styles.searchTypeGroup}>
                            <label className={styles.searchTypeItem}>
                                <input
                                    type="radio"
                                    name="searchType"
                                    value="name"
                                    checked={searchType === 'name'}
                                    onChange={() => {
                                        setSearchType('name')
                                        setSearchKeyword('')
                                    }}
                                />
                                <span>Name</span>
                            </label>
                            <label className={styles.searchTypeItem}>
                                <input
                                    type="radio"
                                    name="searchType"
                                    value="tag"
                                    checked={searchType === 'tag'}
                                    onChange={() => {
                                        setSearchType('tag')
                                        setSearchKeyword('')
                                    }}
                                />
                                <span>Tag</span>
                            </label>
                        </div>
                    </div>

                    {roomListLoading ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
                            <Spin />
                        </div>
                    ) : filteredRoomList.length === 0 ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, color: '#717787', fontSize: 13 }}>
                            No conversations
                        </div>
                    ) : groupedByTag ? (
                        groupedByTag.map((group) => (
                            <div key={group.tag || '__no_tag__'} className={styles.tagGroup}>
                                <div className={styles.tagGroupHeader}>
                                    <span className={styles.tagGroupLabel}>{group.tag || 'No Tag'}</span>
                                    <span className={styles.tagGroupCount}>({group.rooms.length})</span>
                                </div>
                                {group.rooms.map(renderRoomItem)}
                            </div>
                        ))
                    ) : (
                        filteredRoomList.map(renderRoomItem)
                    )}
                </div>
                <div className={styles.centerPanel}>
                    {detailLoading ? (
                        <div className={styles.loadingWrap}>
                            <Spin />
                        </div>
                    ) : roomDetail ? (
                        <div className={styles.chatContainer}>
                            <div className={styles.chatHeader}>
                                <span className={styles.chatNickname}>{roomDetail.nickname}</span>
                                <span className={styles.chatChannel}>
                                    <img
                                        src={typeIconMap[roomDetail.channel]}
                                        alt={roomDetail.channel}
                                        style={{ width: 16, height: 16 }}
                                    />
                                    {roomDetail.channel}
                                </span>
                            </div>
                            <div className={styles.messageList} ref={messageListRef}>
                                {loadingMore && (
                                    <div style={{ textAlign: 'center', padding: '8px 0' }}>
                                        <Spin size="small" />
                                    </div>
                                )}
                                {!hasMore && roomDetail.messages.length > 0 && (
                                    <div style={{ textAlign: 'center', padding: '8px 0', color: '#888', fontSize: 12 }}>
                                        No more messages
                                    </div>
                                )}
                                {(() => {
                                    const canDeleteInThisRoom =
                                        activeTab === 'liveAgent' &&
                                        liveAgentFilter === 'my_chat' &&
                                        roomDetail.channel === 'Telegram'

                                    return [...roomDetail.messages].reverse().map((msg, idx) => {
                                        const isUser = msg.send_type === 'user'
                                        const isAgent = msg.send_type === 'agent'
                                        const isSystem = msg.message?.[0]?.message.type === 'system'
                                        const msgKey = msg.message?.[0]?.msg_id || `${msg.send_time}-${idx}`
                                        if (isSystem) {
                                            return (
                                                <div key={msgKey} className={styles.systemMsgRow}>
                                                    {renderMessageText(msg)}
                                                </div>
                                            )
                                        }
                                        const rightAvatar = isAgent ? agentIcon : robotIcon
                                        const senderLabel = isUser ? 'user' : isAgent ? 'live agent' : 'robot'
                                        const canDeleteThisMsg =
                                            canDeleteInThisRoom &&
                                            isAgent &&
                                            !!msg.message?.[0]?.msg_id &&
                                            nowTick - msg.send_time * 1000 <= MESSAGE_DELETE_WINDOW_MS
                                        return (
                                            <div
                                                key={msgKey}
                                                className={classNames(styles.msgRow, {
                                                    [styles.msgUser]: isUser,
                                                    [styles.msgBot]: !isUser,
                                                })}
                                            >
                                                {isUser && (
                                                    <div className={styles.avatarWrap}>
                                                        <img src={userIcon} alt="user" className={styles.msgAvatar} />
                                                        <span className={classNames(styles.avatarLabel, styles.label_user)}>{senderLabel}</span>
                                                    </div>
                                                )}
                                                <div className={styles.msgBubble}>
                                                    {renderMessageText(msg)}
                                                    <span className={styles.msgTime}>{formatRoomTime(msg.send_time)}</span>
                                                    {canDeleteThisMsg && (
                                                        <Popconfirm
                                                            rootClassName="delete-msg-popconfirm"
                                                            title={<span className="delete-msg-popconfirm-title">Delete <span className="delete-msg-popconfirm-title-highlight">Message</span></span>}
                                                            description="Are you sure? You can't undo this action afterwards."
                                                            okText="Delete"
                                                            cancelText="Cancel"
                                                            placement="topRight"
                                                            okButtonProps={{ danger: true, type: 'primary' }}
                                                            onConfirm={() => handleDeleteMessage(msg)}
                                                        >
                                                            <span className={styles.msgDeleteBtn} title="Delete">
                                                                <SvgIcon name="delete" width="12" height="12" />
                                                            </span>
                                                        </Popconfirm>
                                                    )}
                                                </div>
                                                {!isUser && (
                                                    <div className={styles.avatarWrap}>
                                                        <img src={rightAvatar} alt={senderLabel} className={styles.msgAvatar} />
                                                        <span className={classNames(styles.avatarLabel, styles[`label_${msg.send_type}`])}>{senderLabel}</span>
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    })
                                })()}
                                <div ref={messagesEndRef} />
                            </div>
                            {activeTab === 'liveAgent' && liveAgentFilter === 'my_chat' && (
                                <div className={styles.inputArea}>
                                    {sendProgress && (
                                        <div
                                            style={{
                                                position: 'absolute',
                                                bottom: '100%',
                                                left: 0,
                                                marginBottom: 6,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 6,
                                                padding: '4px 10px',
                                                borderRadius: 8,
                                                background: 'rgba(30, 32, 40, 0.95)',
                                                border: '1px solid rgba(37, 209, 247, 0.25)',
                                                fontSize: 12,
                                                color: '#25D1F7',
                                                pointerEvents: 'none',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            <Spinner size={12} />
                                            <span>
                                                {sendProgress.total > 1
                                                    ? `Sending ${sendProgress.current}/${sendProgress.total}...`
                                                    : 'Sending...'}
                                            </span>
                                        </div>
                                    )}
                                    <Tooltip title="Document" placement="top">
                                        <label className={styles.uploadBtn} htmlFor="docUpload">
                                            <img src={fileIcon} alt="file" width={16} height={16} />
                                            <input
                                                id="docUpload"
                                                type="file"
                                                accept=".pdf,.zip,"
                                                style={{ display: 'none' }}
                                                onChange={handleDocUpload}
                                            />
                                        </label>
                                    </Tooltip>
                                    <div
                                        ref={editableRef}
                                        className={styles.msgInput}
                                        contentEditable
                                        suppressContentEditableWarning
                                        onInput={() => {
                                            if (editableRef.current) {
                                                const text = editableRef.current.textContent?.trim() || ''
                                                const hasImg = !!editableRef.current.querySelector('img')
                                                setHasContent(!!text || hasImg)
                                            }
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault()
                                                handleSend()
                                            }
                                        }}
                                        onPaste={handlePaste}
                                        onClick={(e) => {
                                            const target = e.target as HTMLElement
                                            const url = target.tagName === 'IMG'
                                                ? target.getAttribute('data-image-url')
                                                : null
                                            if (url) {
                                                e.preventDefault()
                                                setPreviewImage(url)
                                            }
                                        }}
                                        data-placeholder="Send Message..."
                                    />
                                    {previewImage && (
                                        <Image
                                            src={previewImage}
                                            style={{ display: 'none' }}
                                            preview={{
                                                visible: true,
                                                src: previewImage,
                                                onVisibleChange: (visible) => {
                                                    if (!visible) setPreviewImage(null)
                                                },
                                            }}
                                        />
                                    )}
                                    <Tooltip title="Image" placement="top">
                                        <label className={styles.uploadBtn} htmlFor="imageUpload">
                                            <img src={photoIcon} alt="photo" width={16} height={16} />
                                            <input
                                                id="imageUpload"
                                                type="file"
                                                style={{ display: 'none' }}
                                                accept="image/*"
                                                onChange={handleFileUpload}
                                            />
                                        </label>
                                    </Tooltip>
                                    <Tooltip title="Audio / Video" placement="top">
                                        <label className={styles.uploadBtn} htmlFor="audioUpload">
                                            <img src={videoIcon} alt="audio" width={16} height={16} />
                                            <input
                                                id="audioUpload"
                                                type="file"
                                                style={{ display: 'none' }}
                                                accept="audio/*,video/*"
                                                onChange={handleAudioUpload}
                                            />
                                        </label>
                                    </Tooltip>
                                    <button
                                        className={styles.sendBtn}
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={handleSend}
                                        disabled={sending || !hasContent}
                                    >
                                        {sending
                                            ? <Spinner size={16} />
                                            : <SvgIcon name="send" width="16" height="16" />}
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className={styles.emptyChat}>Select a conversation</div>
                    )}
                </div>
                <div className={styles.rightPanel}>
                    {roomDetail && (
                        <div className={styles.detailInfo}>
                            <div className={styles.detailUserCard}>
                                <div
                                    className={styles.detailUserAvatar}
                                    style={{ background: getAvatarColor(roomDetail.nickname || '') }}
                                >
                                    {roomDetail.nickname
                                        ?.split(' ')
                                        .map(word => word.charAt(0))
                                        .join('')
                                        .slice(0, 2)
                                        .toUpperCase() || '?'}
                                </div>
                                <div className={styles.detailUserName}>{roomDetail.nickname}</div>
                                <div className={styles.detailUserLastActive}>
                                    {(() => {
                                        const lastUserMsg = roomDetail.messages
                                            .find(m => m.send_type === 'user')
                                        if (!lastUserMsg) return ''
                                        const diff = Math.floor((Date.now() - lastUserMsg.send_time * 1000) / 1000 / 60)
                                        if (diff < 1) return 'Last active just now'
                                        if (diff < 60) return `Last active ${diff} min ago`
                                        const hours = Math.floor(diff / 60)
                                        if (hours < 24) return `Last active ${hours} hr ago`
                                        return `Last active ${Math.floor(hours / 24)} day ago`
                                    })()}
                                </div>
                            </div>
                            <div className={styles.detailSection}>
                                <div className={styles.detailLabel}>Channel</div>
                                <div className={classNames(styles.detailValue, styles.badgeOutline)}>
                                    <img src={typeIconMap[roomDetail.channel]} alt="" style={{ width: 14, height: 14 }} />
                                    {roomDetail.channel}
                                </div>
                            </div>
                            <div className={styles.detailSection}>
                                <div className={styles.detailLabel}>Channel ID</div>
                                <div className={styles.detailValue}>
                                    {roomDetail.channel_id}
                                    <img src={copyIcon} alt="copy" style={{ width: 16, height: 16, cursor: 'pointer' }} onClick={() => handleCopy(roomDetail.channel_id)} />
                                </div>
                            </div>
                            <div className={styles.detailSection}>
                                <div className={styles.detailLabel}>Channel Name</div>
                                <div className={styles.detailValue}>
                                    {roomDetail.channel_name}
                                </div>
                            </div>
                            <div className={styles.detailSection}>
                                <div className={styles.detailLabel}>Robot ID</div>
                                <div className={styles.detailValue}>
                                    {roomDetail.robot_id}
                                    <img src={copyIcon} alt="copy" style={{ width: 16, height: 16, cursor: 'pointer' }} onClick={() => handleCopy(roomDetail.robot_id)} />
                                </div>
                            </div>
                            <div className={styles.detailSection}>
                                <div className={styles.detailLabel}>Robot Name</div>
                                <div className={styles.detailValue}>
                                    {roomDetail.robot_name}
                                </div>
                            </div>
                            <div className={styles.detailSection}>
                                <div className={styles.detailLabel}>User ID</div>
                                <div className={styles.detailValue}>
                                    {roomDetail.user_id}
                                    <img src={copyIcon} alt="copy" style={{ width: 16, height: 16, cursor: 'pointer' }} onClick={() => handleCopy(roomDetail.user_id)} />
                                </div>
                            </div>
                            <div className={styles.detailSection}>
                                <div className={styles.detailLabel}>Agent</div>
                                <div className={classNames(styles.detailValue, styles.badgePurple)}>
                                    {roomDetail.agent_id}
                                </div>
                            </div>
                            <div className={styles.detailSection}>
                                <div className={styles.detailLabel}>Status</div>
                                <div className={classNames(styles.detailValue, styles.badgeGreen)}>
                                    <span className={styles.statusDot} />
                                    {roomDetail.room_status}
                                </div>
                            </div>
                            <div className={styles.detailSection}>
                                <div className={styles.detailLabel}>Follow-up Label</div>
                                <div className={classNames(styles.detailValue, styles.badgeOutline)}>
                                    {activeRoom?.tag || '-'}
                                </div>
                            </div>
                            {activeTab === 'liveAgent' && liveAgentFilter === 'my_chat' && (
                                <div className={styles.tagEditSection}>
                                    <input
                                        className={styles.tagInput}
                                        value={tagInputValue}
                                        onChange={(e) => setTagInputValue(e.target.value)}
                                        placeholder="Enter follow-up label"
                                    />
                                    <button
                                        className={styles.tagSaveBtn}
                                        onClick={handleSaveTag}
                                        disabled={tagSaving}
                                    >
                                        {tagSaving ? 'Saving...' : 'Save'}
                                    </button>
                                </div>
                            )}

                            {activeTab === 'liveAgent' && (
                                liveAgentFilter === 'my_chat'
                                || (liveAgentFilter === 'all_chat' && hasChatManage)
                            ) && (
                                <div className={styles.actionButtons}>
                                    {transferPanelVisible && (
                                        <div className={styles.agentListPanel}>
                                            {agentListLoading ? (
                                                <div className={styles.agentListLoading}>
                                                    <Spin size="small" />
                                                </div>
                                            ) : agentList.length === 0 ? (
                                                <div className={styles.agentListEmpty}>No available agents</div>
                                            ) : (
                                                agentList.map((agent) => (
                                                    <div key={agent} className={styles.agentItem} onClick={() => handleHandover(agent)}>
                                                        <SvgIcon name="live-agent" width="14" height="14" />
                                                        {agent}
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    )}

                                    <button
                                        className={classNames(styles.transferBtn, { [styles.transferBtnActive]: transferPanelVisible })}
                                        onClick={handleTransferClick}
                                    >
                                        <SvgIcon name="update" width="16" height="16" />
                                        Transfer Session
                                    </button>
                                    {liveAgentFilter === 'my_chat' && (
                                        <button className={styles.closeBtn} onClick={handleCloseSession}>
                                            <SvgIcon name="close" width="16" height="16" />
                                            Close Session
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

        </div>
    )
}

export default LiveChat
